import { useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Busy, ConfirmDelete, Field, PageHeader } from '@/components/kit';
import { FormDialog, ListBody } from '@/components/inputs';
import { BackLink } from '@/components/record';
import DataTable, { Dash } from '@/components/admin/DataTable';
import { CREDIT_RATE_FIELDS, DEFAULT_CREDIT_RATES, findRate, rateMoney } from '@/shared/creditRates';
import { committed, rows } from '@/shared/data';

export const route = { path: '/admin/ledger/rates', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

const EMPTY = { Name: '', rate_c: '', notes_c: '' };

function exampleCost(rate) {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `10 credits cost you $${(Math.round(n * 10 * 100) / 100).toFixed(2)}.`;
}

export default function AdminCreditRates() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const { data, loading, error, run } = useFetch(
    () => sdk.table('credit_rates_c').select(CREDIT_RATE_FIELDS).orderBy('Name').limit(200).fetch().then(rows),
    [],
  );

  const list = data ?? [];
  const missing = DEFAULT_CREDIT_RATES.filter((d) => !findRate(list, d.Name));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  }

  function openEdit(rate) {
    setEditing(rate);
    setForm({ Name: rate.Name ?? '', rate_c: rate.rate_c ?? '', notes_c: rate.notes_c ?? '' });
    setDialogOpen(true);
  }

  async function submit() {
    const name = form.Name.trim();
    const rate = Number(form.rate_c);
    if (!name) {
      toast.error('Type the service name');
      return;
    }
    if (form.rate_c === '' || !Number.isFinite(rate) || rate < 0) {
      toast.error('Type the cost per credit, like 1.80');
      return;
    }
    const clash = findRate(list, name);
    if (clash && clash.Id !== editing?.Id) {
      toast.error(`${clash.Name} already has a rate. Edit that one instead.`);
      return;
    }
    const fields = { Name: name, rate_c: rate, notes_c: form.notes_c.trim() };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('credit_rates_c').update({ Id: editing.Id, ...fields })
        : await sdk.table('credit_rates_c').create(fields);
      if (!committed(res, editing ? 'Rate saved' : `${name} added`)) return;
      setDialogOpen(false);
      run();
    } finally {
      setSaving(false);
    }
  }

  async function remove(rate) {
    const res = await sdk.table('credit_rates_c').remove(rate.Id);
    if (!committed(res, `${rate.Name} removed`)) return false;
    run();
    return true;
  }

  async function addStartingRates() {
    if (!missing.length) return;
    setSeeding(true);
    try {
      const res = await sdk.table('credit_rates_c').create(missing.map((d) => ({ Name: d.Name, rate_c: d.rate_c })));
      if (committed(res, 'Starting rates added')) run();
    } finally {
      setSeeding(false);
    }
  }

  const columns = [
    {
      label: 'Service',
      render: (r) => (
        <button type="button" onClick={() => openEdit(r)} className="text-left font-medium text-primary hover:underline">
          {r.Name}
        </button>
      ),
    },
    {
      label: 'Cost per credit',
      className: 'text-right tabular-nums',
      render: (r) => <span className="font-semibold">{rateMoney(r.rate_c)}</span>,
    },
    {
      label: '10 credits',
      className: 'hidden sm:table-cell text-right tabular-nums text-muted-foreground',
      render: (r) => (Number.isFinite(Number(r.rate_c)) ? `$${(Number(r.rate_c) * 10).toFixed(2)}` : <Dash />),
    },
    {
      label: 'Notes',
      className: 'hidden md:table-cell',
      render: (r) => (r.notes_c ? <span className="line-clamp-1 max-w-xs text-sm text-muted-foreground">{r.notes_c}</span> : <Dash />),
    },
    {
      label: 'Actions',
      className: 'w-0 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Edit ${r.Name}`} onClick={() => openEdit(r)}>
            <ApperIcon name="Pencil" size={16} />
          </Button>
          <ConfirmDelete
            title={`Remove ${r.Name}?`}
            description="Ledger entries already saved keep their cost. New entries for this service will need the cost typed in."
            onConfirm={() => remove(r)}
            variant="ghost"
          />
        </div>
      ),
    },
  ];

  const startingText = DEFAULT_CREDIT_RATES.map((d) => `${d.Name} ${rateMoney(d.rate_c)}`).join(', ');

  return (
    <div className="mx-auto max-w-4xl">
      <BackLink to="/admin/ledger" label="Back to Ledger" />
      <PageHeader
        eyebrow="Ledger"
        title="Credit rates"
        description="What one credit costs you, per service. Ledger entries work out cost as credits used × this rate, and you can still change it on any entry."
        actions={
          <>
            {data !== undefined && missing.length > 0 && (
              <Button variant="outline" onClick={addStartingRates} disabled={seeding}>
                <Busy
                  busy={seeding}
                  label={
                    <>
                      <ApperIcon name="Layers" size={16} />
                      Add starting rates
                    </>
                  }
                  busyLabel="Adding…"
                />
              </Button>
            )}
            <Button onClick={openNew}>
              <ApperIcon name="Plus" size={16} />
              Add service
            </Button>
          </>
        }
      />

      {data !== undefined && !list.length && (
        <p className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {`No rates saved yet, so the ledger uses the starting rates: ${startingText}. Add them here to edit them, or add your own services.`}
        </p>
      )}

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Coins',
          title: 'No services yet',
          description: `Add each service you buy credits for with its cost per credit. Starting rates: ${startingText}.`,
          action: missing.length ? addStartingRates : openNew,
          actionLabel: missing.length ? 'Add starting rates' : 'Add service',
        }}
      >
        <DataTable columns={columns} rows={list} />
      </ListBody>

      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? `Edit ${editing.Name}` : 'Add a service'}
        description="The cost per credit is in USD."
        onSubmit={submit}
        saving={saving}
        submitLabel={editing ? 'Save rate' : 'Add service'}
      >
        <Field label="Service name" htmlFor="rate-name" hint="Match the plan or product name and entries pick it up by themselves.">
          <Input id="rate-name" value={form.Name} onChange={set('Name')} placeholder="Banshee" maxLength={80} />
        </Field>
        <Field label="Cost per credit (USD)" htmlFor="rate-value" hint={exampleCost(form.rate_c) || undefined}>
          <Input
            id="rate-value"
            type="number"
            min={0}
            step="0.0001"
            inputMode="decimal"
            value={form.rate_c}
            onChange={set('rate_c')}
            placeholder="1.80"
          />
        </Field>
        <Field label="Notes" htmlFor="rate-notes">
          <Textarea id="rate-notes" rows={2} value={form.notes_c} onChange={set('notes_c')} placeholder="Reseller panel, bought in packs of 100…" />
        </Field>
      </FormDialog>
    </div>
  );
}
